import "../wdyr";
import "../styles/globals.css";

import { ThemeProvider as MuiThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { enUS } from "@mui/x-date-pickers-pro/locales";
import { LicenseInfo } from "@mui/x-license";
import axios from "axios";
import dayjs from "dayjs";
import dayjsLocalizedFormat from "dayjs/plugin/localizedFormat";
import dayjsRelativeTime from "dayjs/plugin/relativeTime";
import dayjsTimezone from "dayjs/plugin/timezone";
import dayjsUtc from "dayjs/plugin/utc";
import { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { SnackbarProvider } from "notistack";
import qs from "qs";
import React, { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "react-query";

import { getMuiTheme } from "../common/theme";
import { GoogleMapsApiLoader } from "../components/form/AddressAutocomplete/GoogleMapsApiLoader";
import { AccountClosureBanner } from "../components/layout/AccountClosureBanner";
import { PlatformClosureBanner } from "../components/layout/PlatformClosureBanner";
import { AuthedAppContextProvider } from "../contexts/app/AuthedAppContextProvider";
import { ExchangeRateSubscriptionContextProvider } from "../contexts/ExchangeRateSubscriptionContext";
import { SidePanelContextProvider } from "../contexts/SidePanelContext";
import { NextPageWithLayout } from "../NextPageWithLayout";
import { QUERY_DEFAULT_OPTIONS } from "../queries/defaults";
import { AuthProvider } from "../services/auth";
import { LazyUnitTokenFetchingContextProvider } from "../services/LazyUnitTokenFetchingContext";
import { UrqlClientProvider } from "../urql/UrqlClientProvider";

// Layout types.
type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

// Set up axios.
axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL;
axios.defaults.paramsSerializer = (params) => qs.stringify(params, { arrayFormat: "repeat" });

// Set up dayjs.
dayjs.extend(dayjsLocalizedFormat);
dayjs.extend(dayjsRelativeTime);
dayjs.extend(dayjsTimezone);
dayjs.extend(dayjsUtc);

// cast `as any` to fix QueryClient typing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const queryClient = new QueryClient({ defaultOptions: QUERY_DEFAULT_OPTIONS as any });
const OMI_MODE = Boolean(parseInt(process.env.NEXT_PUBLIC_OMI_MODE ?? "0"));
const FAVICON_URL = OMI_MODE ? "/favicon-omi.ico" : "/favicon.ico";

function App({ Component, pageProps }: AppPropsWithLayout) {
    const { getLayout = (page) => page } = Component;

    useEffect(() => {
        const licenseKey = process.env.NEXT_PUBLIC_MUI_X_PRO_LICENSE_KEY;
        if (licenseKey != null) {
            LicenseInfo.setLicenseKey(licenseKey);
        }
    }, []);

    return (
        <AuthProvider>
            <UrqlClientProvider>
                <QueryClientProvider client={queryClient}>
                    <LazyUnitTokenFetchingContextProvider>
                        <Head>
                            <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
                            <link rel="shortcut icon" href={FAVICON_URL} />
                            <title>Levro</title>
                        </Head>
                        {/* https://help.alloy.com/hc/en-us/articles/4405646102043-iovation-Integration-Device-Data-Aggregation- */}
                        <Script src="/iovation/config.js" />
                        <Script src="/iovation/iovation.js" />
                        <Script src="https://mpsnare.iesnare.com/snare.js" />
                        <GoogleMapsApiLoader>
                            <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                localeText={{
                                    ...enUS.components.MuiLocalizationProvider.defaultProps.localeText,
                                    start: "From",
                                    end: "To",
                                }}
                            >
                                <MuiThemeProvider theme={getMuiTheme()}>
                                    <SnackbarProvider
                                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                        autoHideDuration={3000}
                                    >
                                        <AuthedAppContextProvider>
                                            <ExchangeRateSubscriptionContextProvider>
                                                <SidePanelContextProvider>
                                                    <AccountClosureBanner />
                                                    <PlatformClosureBanner />
                                                    {getLayout(<Component {...pageProps} />)}
                                                </SidePanelContextProvider>
                                            </ExchangeRateSubscriptionContextProvider>
                                        </AuthedAppContextProvider>
                                    </SnackbarProvider>
                                </MuiThemeProvider>
                            </LocalizationProvider>
                        </GoogleMapsApiLoader>
                    </LazyUnitTokenFetchingContextProvider>
                </QueryClientProvider>
            </UrqlClientProvider>
        </AuthProvider>
    );
}

export default App;
